import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';

import { useFirmUserList } from 'hooks/useFirmUserList';
import { generateFullName } from 'utils/generateFullNameUtils';
import { handleRedirection } from 'utils/utils';
import { handleSettingSkAndPkToLocalStorage } from 'utils/localStorageUtils';
import ImageComponent from 'shared/ImageComponent';
import GlobalTooltip from 'components/GlobalTooltip';

export default function TeamMember({ selectedList, setSelectedList, leadDetails }) {
  const history = useHistory();
  const { userList } = useFirmUserList(true, true);

  const usersOnly = userList?.filter((user) => user?.res_type === 'user');

  useEffect(() => {
    if (!userList || userList.length === 0) return;

    if (selectedList && selectedList.length > 0 && selectedList.some((item) => item?.type)) {
      return;
    }

    const existingTeamMembers = selectedList?.filter((member) => member?.firm_user_id || member?.user_group_id) || [];

    const modifiedExistingMembers = existingTeamMembers.map((member) => ({
      ...member,
      type: member?.user_group_id ? 'user-group' : 'user',
    }));

    const existingUserIds = new Set(existingTeamMembers.map((m) => m?.firm_user_id).filter(Boolean));
    const existingGroupIds = new Set(existingTeamMembers.map((m) => m?.user_group_id).filter(Boolean));

    const newUsers = userList
      ?.filter((user) => user?.res_type !== 'user-group' && !existingUserIds.has(user?.firm_user_id))
      ?.map((user) => ({
        ...user,
        type: 'user',
      }));

    const newGroups = userList
      ?.filter((group) => group?.res_type === 'user-group' && !existingGroupIds.has(group?.user_group_id))
      ?.map((group) => ({
        ...group,
        type: 'user-group',
      }));

    const leadAssignee = leadDetails?.lead_assign_to?.[0];
    if (leadAssignee && !existingUserIds.has(leadAssignee?.firm_user_id)) {
      const leadAssigneeUser = userList?.find((user) => user?.firm_user_id === leadAssignee?.firm_user_id);
      if (leadAssigneeUser) {
        const leadAssigneeIndex = newUsers?.findIndex((user) => user?.firm_user_id === leadAssignee?.firm_user_id);
        if (leadAssigneeIndex !== -1) {
          newUsers[leadAssigneeIndex] = {
            ...newUsers[leadAssigneeIndex],
            ...leadAssignee,
            is_selected: true,
            derived_from_group: false,
          };
        }
      }
    }

    const allItems = [...modifiedExistingMembers, ...(newUsers || []), ...(newGroups || [])];
    setSelectedList(allItems);
  }, [userList, leadDetails?.lead_assign_to]);

  const onChangeHandler = (e, roleName = null) => {
    setSelectedList((preVal) => {
      // Handle PrimeReact Dropdown event (e.value) vs Checkbox event (e.target.value)
      const isDropdownEvent = roleName !== null;
      const selectedValue = isDropdownEvent ? e.value : e.target.value;
      const fieldName = isDropdownEvent ? roleName : e.target.name;

      // If dropdown value is cleared (null/undefined), clear all role assignments for that role
      if (isDropdownEvent && (selectedValue === null || selectedValue === undefined)) {
        let clearedList = preVal?.map((val) => ({
          ...val,
          [fieldName]: false,
        }));

        // After clearing, only uncheck users who had this role and now have no role assignments
        // Don't uncheck users who were manually selected (is_selected: true but no role assignments)
        return clearedList?.map((val) => {
          if (val?.type === 'user' && val?.firm_user_id) {
            const hasRoleAssignment =
              val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

            // Check if user is in a selected group
            const userGroups = val?.user_group || [];
            const selectedGroups = clearedList?.filter((v) => v?.type === 'user-group' && v?.is_selected) || [];
            const isUserInSelectedGroup = userGroups.some((userGroup) => selectedGroups.some((group) => group?.sk === userGroup?.sk));

            // Only uncheck if user had this role and now has no role assignments
            // If user was manually selected (is_selected: true but no role assignments), keep it checked
            const hadThisRole = preVal?.find((v) => v?.firm_user_id === val?.firm_user_id)?.[fieldName] === true;

            if (hasRoleAssignment || isUserInSelectedGroup) {
              return { ...val, is_selected: true };
            } else if (hadThisRole && !hasRoleAssignment) {
              // User had this role via dropdown, now it's cleared, so uncheck
              return { ...val, is_selected: false };
            }
            // User was manually selected or has other reasons to stay checked, keep current state
            return val;
          }
          return val;
        });
      }

      let updatedList = preVal?.map((val) => {
        if (val?.firm_user_id || val?.user_group_id) {
          if (val?.firm_user_id === selectedValue || val?.user_group_id === selectedValue) {
            if (fieldName === 'is_selected') {
              if (!e.checked) {
                return {
                  ...val,
                  [fieldName]: e.checked,
                  is_lead_associate: false,
                  is_lead_attorney: false,
                  is_lead_paralegal: false,
                  is_lead_case_manager: false,
                };
              } else if (e.checked && !e.target.isGroup) {
                return { ...val, [fieldName]: e.checked, derived_from_group: false };
              } else {
                return { ...val, [fieldName]: e.checked };
              }
            } else {
              // For dropdown role selection, set the role for the selected user
              return {
                ...val,
                is_lead_associate: false,
                is_lead_attorney: false,
                is_lead_paralegal: false,
                is_lead_case_manager: false,
                [fieldName]: true,
                is_selected: true,
                derived_from_group: e.target?.isGroup ? undefined : false,
              };
            }
          } else if (fieldName !== 'is_selected' && val?.[fieldName] === true) {
            // Clear the role flag for any other user who previously had this role
            // Store that this user had a role assignment (for later checking)
            return {
              ...val,
              [fieldName]: false,
            };
          }
        } else if (fieldName !== 'is_selected') {
          return { ...val, [fieldName]: false };
        }
        return { ...val };
      });

      // After dropdown changes, update is_selected for users who had role assignments
      // Only uncheck users who had a role via dropdown and now don't have any roles
      // Don't uncheck users who were manually selected (is_selected: true but no role assignments)
      if (isDropdownEvent) {
        updatedList = updatedList?.map((val) => {
          if (val?.type === 'user' && val?.firm_user_id) {
            const hasRoleAssignment =
              val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

            // Check if user is in a selected group
            const userGroups = val?.user_group || [];
            const selectedGroups = updatedList?.filter((v) => v?.type === 'user-group' && v?.is_selected) || [];
            const isUserInSelectedGroup = userGroups.some((userGroup) => selectedGroups.some((group) => group?.sk === userGroup?.sk));

            // Check if user had the role that was just changed
            const previousUser = preVal?.find((v) => v?.firm_user_id === val?.firm_user_id);
            const hadChangedRole = previousUser?.[fieldName] === true;

            if (hasRoleAssignment) {
              // User has a role assignment, ensure checkbox is checked
              return { ...val, is_selected: true, derived_from_group: false };
            } else if (isUserInSelectedGroup) {
              // User is in a selected group, keep current is_selected state
              return val;
            } else if (hadChangedRole && !hasRoleAssignment) {
              // User had this role via dropdown, now it's changed/removed, so uncheck
              return { ...val, is_selected: false };
            }
            // User was manually selected or has other reasons to stay checked, keep current state
            return val;
          }
          return val;
        });
      }

      if (fieldName === 'is_selected') {
        updatedList = updatedList?.map((val) => {
          if (val?.type === 'user') {
            const userGroups = val?.user_group || [];
            const selectedGroups = updatedList?.filter((v) => v?.type === 'user-group' && v?.is_selected) || [];

            const isUserInSelectedGroup = userGroups.some((userGroup) => selectedGroups.some((group) => group?.sk === userGroup?.sk));

            if (fieldName === 'is_selected' && e.target?.isGroup) {
              if (e.checked) {
                const belongsToClickedGroup = userGroups.some((userGroup) =>
                  selectedGroups.some((group) => group?.sk === userGroup?.sk && group?.user_group_id === selectedValue)
                );

                if (belongsToClickedGroup) {
                  const hasRoleAssignment =
                    val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

                  if (val?.derived_from_group === false || hasRoleAssignment === true) {
                    return { ...val, is_selected: true, derived_from_group: false };
                  } else {
                    return {
                      ...val,
                      is_selected: true,
                      derived_from_group: true,
                    };
                  }
                }
              } else {
                const wasDerivedFromClickedGroup =
                  val?.derived_from_group === true &&
                  userGroups.some((userGroup) =>
                    selectedGroups.some((group) => group?.sk === userGroup?.sk && group?.user_group_id === selectedValue)
                  );

                if (wasDerivedFromClickedGroup) {
                  const stillInOtherGroups = userGroups.some((userGroup) =>
                    selectedGroups.some((group) => group?.sk === userGroup?.sk && group?.user_group_id !== selectedValue)
                  );

                  if (!stillInOtherGroups) {
                    const hasRoleAssignment =
                      val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

                    if (hasRoleAssignment) {
                      return { ...val, is_selected: true, derived_from_group: false };
                    } else {
                      return {
                        ...val,
                        is_selected: false,
                      };
                    }
                  }
                }
              }
            }

            if (isUserInSelectedGroup) {
              const hasRoleAssignment =
                val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

              if (val?.derived_from_group === false || hasRoleAssignment) {
                return { ...val, is_selected: true, derived_from_group: false };
              } else {
                return { ...val, is_selected: true, derived_from_group: true };
              }
            } else {
              if (val?.derived_from_group === true) {
                const hasRoleAssignment =
                  val?.is_lead_attorney || val?.is_lead_associate || val?.is_lead_paralegal || val?.is_lead_case_manager;

                if (hasRoleAssignment) {
                  return { ...val, is_selected: true, derived_from_group: false };
                } else {
                  return {
                    ...val,
                    is_selected: false,
                  };
                }
              }
            }
          }
          return val;
        });
      }

      return updatedList;
    });
  };

  const getSelectedItem = (name) => {
    return selectedList?.find((v) => v?.[name] === true && !v?.group_name)?.firm_user_id;
  };

  const isUserInSelectedGroup = (rowData) => {
    return (
      rowData?.type === 'user' &&
      selectedList?.some(
        (group) =>
          group?.type === 'user-group' && group?.is_selected && rowData?.user_group?.find((userGroup) => userGroup?.sk === group?.sk)
      )
    );
  };

  const nameTemplate = (rowData) => {
    const { sk, pk, firm_user_id } = rowData;
    let url = '/settings/team-management';
    let stateToPass = {};

    if (firm_user_id) {
      url = firm_user_id ? `/settings/team-management/${firm_user_id}` : '/settings/team-management';
      stateToPass = rowData;
    }

    return (
      <div
        className="d-flex w-100"
        onClick={() => {
          handleRedirection(history, url, stateToPass);
          handleSettingSkAndPkToLocalStorage(sk, pk);
        }}
      >
        <span className="p-column-title text-break d-none">Lead Attorney</span>
        <ImageComponent filePath={rowData?.profile_image} fileName={rowData?.first_name} fileSize="small.jpg" className="col-1 me-2" />
        <span className="d-flex align-items-center text-break redirection-link-no-text-transform">
          {rowData?.group_name || generateFullName(rowData)}
        </span>
      </div>
    );
  };

  const selectColumnTemplate = (rowData) => {
    const userInSelectedGroup = isUserInSelectedGroup(rowData);
    const isDisabled = !(rowData.status === 'ACTIVE') || userInSelectedGroup;

    return (
      <GlobalTooltip tooltip={userInSelectedGroup ? 'User is already selected through a group' : ''} position="top">
        <Checkbox
          id="is_selected"
          checked={rowData?.is_selected}
          name="is_selected"
          value={rowData.firm_user_id || rowData.user_group_id}
          onChange={(e) => {
            e.target.isGroup = rowData?.type === 'user-group';
            onChangeHandler(e);
          }}
          disabled={isDisabled}
          style={isDisabled ? { opacity: 0.5 } : undefined}
        />
      </GlobalTooltip>
    );
  };

  const rowheaderTemplate = (data) => {
    return (
      <React.Fragment>
        <strong>{data?.type === 'user-group' ? 'Groups' : 'Users'}</strong>
      </React.Fragment>
    );
  };

  return (
    <div className="card-bg p-3 billing-preferences F-size14">
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-12 col-md-4 p-medium">Lead Attorney</div>
        <div className="col-12 col-md-8">
          <Dropdown
            name="is_lead_attorney"
            optionLabel={(v) => generateFullName(v)}
            optionValue="firm_user_id"
            options={usersOnly}
            onChange={(e) => onChangeHandler(e, 'is_lead_attorney')}
            className="input-shadow w-100"
            value={getSelectedItem('is_lead_attorney')}
            filter
            resetFilterOnHide
          />
        </div>
      </div>

      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-12 col-md-4 p-medium">Lead Associate</div>
        <div className="col-12 col-md-8">
          <Dropdown
            name="is_lead_associate"
            optionLabel={(v) => generateFullName(v)}
            optionValue="firm_user_id"
            options={usersOnly}
            onChange={(e) => onChangeHandler(e, 'is_lead_associate')}
            className="input-shadow w-100"
            value={getSelectedItem('is_lead_associate')}
            filter
            resetFilterOnHide
          />
        </div>
      </div>
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-12 col-md-4 p-medium">Lead Paralegal</div>
        <div className="col-12 col-md-8">
          <Dropdown
            name="is_lead_paralegal"
            optionLabel={(v) => generateFullName(v)}
            optionValue="firm_user_id"
            options={usersOnly}
            onChange={(e) => onChangeHandler(e, 'is_lead_paralegal')}
            className="input-shadow w-100"
            value={getSelectedItem('is_lead_paralegal')}
            filter
            resetFilterOnHide
          />
        </div>
      </div>
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-12 col-md-4 p-medium">Lead Case Manager</div>
        <div className="col-12 col-md-8">
          <Dropdown
            name="is_lead_case_manager"
            optionLabel={(v) => generateFullName(v)}
            optionValue="firm_user_id"
            options={usersOnly}
            onChange={(e) => onChangeHandler(e, 'is_lead_case_manager')}
            className="input-shadow w-100"
            value={getSelectedItem('is_lead_case_manager')}
            filter
            resetFilterOnHide
          />
        </div>
      </div>
      <div className="d-flex mb-3">
        <div className="col-12">
          <DataTable
            value={selectedList}
            responsiveLayout="scroll"
            emptyMessage="No Data Found."
            selectionMode="checkbox"
            className="case-team-member"
            groupField="type"
            rowGroupMode="subheader"
            rowGroupHeaderTemplate={rowheaderTemplate}
            rowGroupFooterTemplate={() => <></>}
          >
            <Column body={selectColumnTemplate} style={{ width: 25 }} headerStyle={{ width: 25 }} />
            <Column field="name" header="Name" className="add-contact-name" body={nameTemplate} />
          </DataTable>
        </div>
      </div>
    </div>
  );
}
